import type {
  ClassroomQuery,
  ClassroomsQuery,
  OrganizationQuery,
} from '@innovamat/glow-api-client';
import {
  useClassroomQuery,
  useClassroomsQuery,
  useOrganizationQuery,
} from '@innovamat/glow-api-client';
import type { QueryClient } from '@tanstack/react-query';

type UpdateClassroomsCountParams = {
  queryClient: QueryClient;
  organizationId: string;
  updates: Array<{
    classroomId: string;
    countChange: number;
  }>;
};

type UpdateOrganizationCountParams = {
  queryClient: QueryClient;
  organizationId: string;
  studentsChange: number;
  deletedStudentsChange?: number;
};

type UpdateSingleClassroomCountParams = {
  queryClient: QueryClient;
  classroomId: string;
  organizationId: string;
  countChange: number;
};

function updateClassroomsCount({
  queryClient,
  organizationId,
  updates,
}: UpdateClassroomsCountParams): void {
  const updateClassroomsData = (mine: boolean): void => {
    const classroomsKey = useClassroomsQuery.getKey({
      mine,
      orgId: organizationId,
    });

    const classrooms = queryClient.getQueryData<ClassroomsQuery>(classroomsKey);

    if (classrooms) {
      const newData = classrooms.classrooms?.map((classroom) => {
        const update = updates.find((u) => u.classroomId === classroom?.id);

        if (update) {
          const newCount =
            (classroom?.numberOfStudents ?? 0) + update.countChange;
          return {
            ...classroom,
            numberOfStudents: Math.max(0, newCount),
          };
        }

        return classroom;
      });

      queryClient.setQueryData(classroomsKey, {
        classrooms: newData,
      });
    }
  };

  updateClassroomsData(true);
  updateClassroomsData(false);
}

function updateOrganizationCount({
  queryClient,
  organizationId,
  studentsChange,
  deletedStudentsChange = 0,
}: UpdateOrganizationCountParams): void {
  const organizationKey = useOrganizationQuery.getKey({
    id: organizationId,
  });

  const organization =
    queryClient.getQueryData<OrganizationQuery>(organizationKey);

  if (organization?.organization) {
    const currentStudents = organization.organization.numberOfStudents ?? 0;
    const currentDeletedStudents =
      organization.organization.numberOfDeletedStudents ?? 0;

    queryClient.setQueryData<OrganizationQuery>(organizationKey, {
      organization: {
        ...organization.organization,
        numberOfStudents: Math.max(0, currentStudents + studentsChange),
        numberOfDeletedStudents: Math.max(
          0,
          currentDeletedStudents + deletedStudentsChange
        ),
      },
    });
  }
}

function updateSingleClassroomCount({
  queryClient,
  classroomId,
  organizationId,
  countChange,
}: UpdateSingleClassroomCountParams): void {
  const classroomKey = useClassroomQuery.getKey({
    id: classroomId,
  });

  const classroom = queryClient.getQueryData<ClassroomQuery>(classroomKey);

  if (classroom?.classroom) {
    const currentCount = classroom.classroom.numberOfStudents ?? 0;

    queryClient.setQueryData<ClassroomQuery>(classroomKey, {
      classroom: {
        ...classroom.classroom,
        numberOfStudents: Math.max(0, currentCount + countChange),
      },
    });
  }
}

export {
  updateClassroomsCount,
  updateOrganizationCount,
  updateSingleClassroomCount,
};
export type {
  UpdateClassroomsCountParams,
  UpdateOrganizationCountParams,
  UpdateSingleClassroomCountParams,
};
